import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

import AdsBanner, { BannerPosition } from "@components/ads-banner/ads-banner";
import Button from "@components/button/button";
import AnimateCoins from "@components/coins/animate-coins";
import Coins from "@components/coins/coins";
import ChevronRoundedLastExercisedSVG from "@components/icons/chevron-rounded-last-exercised-svg";
import CoinGreySvg from "@components/icons/coin-grey-svg";
import CoinSvg from "@components/icons/coin-svg";
import DoneSvg from "@components/icons/done-svg";
import IndicatorSVG from "@components/icons/indicator-svg";
import LockSVG from "@components/icons/lock-svg";
import MsLightningGreySvg from "@components/icons/ms-lightning-grey-svg";
import MsLightningSvg from "@components/icons/ms-lightning-svg";
import MsStarkyGreySvg from "@components/icons/ms-starky-grey-svg";
import MsStarkySvg from "@components/icons/ms-starky-svg";
import MsTargetGreySvg from "@components/icons/ms-target-grey-svg";
import MsTargetSvg from "@components/icons/ms-target-svg";
import RemoveAdsLink from "@components/remove-ads-link/remove-ads-link";
import WrapperAdsBanner from "@components/wrapper-ads-banner/WrapperAdsBanner";

import { useAppDispatch } from "@hooks/use-store";
import { setIsAdsScriptsReloaded } from "@store/app/app.slice";
import { speed2CurrentUnits } from "@helpers/helpers";
import { Award, IUserExercise } from "@typings/global.types";

type PropsType = {
  title: string;
  star: Award;
  lightning: Award;
  target: Award;
  errors: number;
  speed: number;
  currentExerciseId: number;
  lastExerciseId: number;
  coins: number;
  coinsBeforeStart: number;
  coinsForLesson: number;
  gameMode: boolean;
  speedRatio: number;
  isRemoveAdsLinkVisible: boolean;
  onClickPrevExercises: () => void;
  onClickNextExercises: () => void;
  exercisesList: IUserExercise[];
};

function ExerciseResults({
  title,
  star,
  target,
  lightning,
  errors,
  speed,
  coins,
  coinsBeforeStart,
  coinsForLesson,
  gameMode,
  speedRatio,
  isRemoveAdsLinkVisible,
  onClickPrevExercises,
  onClickNextExercises,
  exercisesList,
  currentExerciseId,
  lastExerciseId,
}: PropsType) {
  const dispatch = useAppDispatch();
  const [specialWidth, setSpecialWidth] = useState<boolean>(false);
  const [btnNextActive, setBtnNextActive] = useState<boolean>(true);
  const [btnPrevActive, setBtnPrevActive] = useState<boolean>(false);
  const btnPrevRef = useRef<HTMLButtonElement>(null);
  const btnNextRef = useRef<HTMLButtonElement>(null);

  const coverBlock = useRef<HTMLDivElement>(null);

  const starTextColor = star.received
    ? "text-green dark:text-green-dark-default"
    : "text-grey dark:text-grey-dark-default";
  const starIconActive = star.received
    ? "fill-green dark:fill-green-dark-default"
    : "fill-grey dark:fill-grey-dark-default";

  const targetTextColor = target.received
    ? "text-orange dark:text-orange-dark-default"
    : "text-grey dark:text-grey-dark-default";
  const targetIconActive = target.received
    ? "fill-orange dark:fill-orange-dark-default"
    : "fill-grey dark:fill-grey-dark-default";

  const lightningTextColor = lightning.received
    ? "text-purple dark:text-purple-dark-default"
    : "text-grey dark:text-grey-dark-default";
  const lightningIconActive = lightning.received
    ? "fill-purple dark:fill-purple-dark-default"
    : "fill-grey dark:fill-grey-dark-default";

  useEffect(() => {
    const followWidth = (): void => {
      const coverBlockWidth =
        coverBlock.current && coverBlock.current!.clientWidth;
      if (coverBlockWidth && exercisesList && exercisesList.length) {
        if (
          coverBlockWidth <=
          exercisesList.length * 2.5 + exercisesList.length * 30 + 30
        ) {
          setSpecialWidth(true);
        } else {
          setSpecialWidth(false);
        }
      }
    };
    setBtnNextActive(true);
    followWidth();
    window.addEventListener("resize", followWidth);

    return () => window.removeEventListener("resize", followWidth);
  }, [exercisesList]);
  useEffect(() => {
    dispatch(setIsAdsScriptsReloaded(true));
  }, [dispatch]);
  useEffect(() => {
    const hotKey = (event: any): void => {
      if (event.keyCode === 39) {
        // next
        setBtnNextActive(true);
        setBtnPrevActive(false);
        btnNextRef.current?.focus();
      }
      if (event.keyCode === 37) {
        setBtnNextActive(false);
        setBtnPrevActive(true);
        btnPrevRef.current?.focus();
      }
      if (event.keyCode === 32 || event.keyCode === 13) {
        if (btnNextActive) {
          if (onClickNextExercises) onClickNextExercises();
        }
        if (btnPrevActive) {
          if (onClickPrevExercises) onClickPrevExercises();
        }
      }
    };
    window.addEventListener("keydown", hotKey);

    return () => window.removeEventListener("keydown", hotKey);
  }, [
    btnNextActive,
    btnPrevActive,
    onClickNextExercises,
    onClickPrevExercises,
  ]);

  return (
    <section ref={coverBlock} className='relative mb-6 text-center md:mb-8'>
      <div className='mx-auto my-4 flex items-center justify-center md:mt-6'>
        <div
          className={classNames("relative flex items-center", {
            "w-[calc(100%-15px)] justify-between": specialWidth,
          })}
        >
          {exercisesList &&
            exercisesList.map((item) => {
              return (
                <div
                  key={item.id}
                  className={classNames("relative mr-[2.5px]", {
                    "!w-[44px]": item.id === currentExerciseId && specialWidth,
                    "!mr-0 h-[30px] w-[15px]": specialWidth,
                  })}
                  style={{
                    zIndex: `${item.orderId}`,
                  }}
                >
                  <div
                    className={classNames("rounded-full bg-transparent", {
                      " top-[-15px]":
                        item.id === currentExerciseId && specialWidth,
                      absolute: specialWidth,
                    })}
                  >
                    {item.active ? (
                      // eslint-disable-next-line
                      <>
                        {item.isLastString &&
                        !(item.star || item.target || item.lightning) ? (
                          <ChevronRoundedLastExercisedSVG
                            width={30}
                            height={30}
                          />
                        ) : (
                          <IndicatorSVG
                            classNameProps=''
                            starEarned={star.earned && !star.received}
                            targetEarned={target.earned && !target.received}
                            lightningEarned={
                              lightning.earned && !lightning.received
                            }
                            star={item.star}
                            target={item.target}
                            lightning={item.lightning}
                            width={item.id === currentExerciseId ? 60 : 30}
                            height={item.id === currentExerciseId ? 60 : 30}
                            active={item.id === currentExerciseId}
                          />
                        )}
                      </>
                    ) : (
                      <LockSVG classNameProps='h-[25px] w-auto md:h-[30px]' />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <h2
        className='mx-auto mb-0 max-w-[655px]'
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <div className='relative mx-auto max-w-screen-container-xl'>
        <p className='dark:text-txt-dark-secondary text-txt-secondary'>
          <b> {speed2CurrentUnits(Math.round(speed), speedRatio)}</b>{" "}
          <FormattedMessage id='wpm' />, <b>{errors}</b>{" "}
          <FormattedMessage
            id='errors'
            values={{
              count: errors,
            }}
          />
        </p>
        {gameMode ? (
          <AnimateCoins
            coins={coinsBeforeStart}
            currentCoins={coins - coinsBeforeStart - coinsForLesson}
          />
        ) : null}

        {target && lightning && star ? (
          <div className='pt-lg mx-auto mb-5 flex max-w-[390px] justify-between'>
            <div className='flex h-full w-2/6 flex-col justify-between px-3'>
              {star.received ? (
                <img
                  className='mx-auto'
                  src='/static/i/svg/ms-starky.svg?v=2'
                  alt='ms-starky'
                />
              ) : (
                <MsStarkyGreySvg classNameProps='mx-auto text-grey dark:text-grey-dark-default opacity-50' />
              )}
              <div className='flex justify-center font-bold'>
                {gameMode ? (
                  // eslint-disable-next-line
                  <>
                    {star.earned ? (
                      <DoneSvg
                        width={30}
                        height={30}
                        classNameProps='inline-block'
                        colorFill={starIconActive}
                      />
                    ) : (
                      <>
                        {star.received ? (
                          <CoinSvg classNameProps='mr-1 w-[30px]' />
                        ) : (
                          <CoinGreySvg classNameProps='mr-1 w-[30px]' />
                        )}
                        <span
                          className={classNames("text-base", starTextColor)}
                        >
                          +{star.coin}
                        </span>
                      </>
                    )}
                  </>
                ) : null}
              </div>
              <div
                className={classNames(
                  "text-small-xs md:text-small",
                  starTextColor,
                )}
              >
                <FormattedMessage
                  id='exercise-results-star'
                  values={{
                    tag: <br />,
                    errors: star.error,
                  }}
                />
              </div>
            </div>
            <div className='flex h-full w-2/6 flex-col justify-between px-3'>
              {target.received ? (
                <img
                  className='mx-auto'
                  src='/static/i/svg/ms-target.svg?v=2'
                  alt='ms-target'
                />
              ) : (
                <MsTargetGreySvg classNameProps='mx-auto text-grey dark:text-grey-dark-default opacity-50' />
              )}
              <div className='flex justify-center font-bold'>
                {gameMode ? (
                  // eslint-disable-next-line
                  <>
                    {target.earned ? (
                      <DoneSvg
                        width={30}
                        height={30}
                        classNameProps='inline-block'
                        colorFill={targetIconActive}
                      />
                    ) : (
                      <>
                        {target.received ? (
                          <CoinSvg classNameProps='mr-1 w-[30px]' />
                        ) : (
                          <CoinGreySvg classNameProps='mr-1 w-[30px]' />
                        )}
                        <span
                          className={classNames("text-base", targetTextColor)}
                        >
                          +{target.coin}
                        </span>
                      </>
                    )}
                  </>
                ) : null}
              </div>
              <div
                className={classNames(
                  "text-small-xs md:text-small",
                  targetTextColor,
                )}
              >
                <FormattedMessage
                  id='exercise-results-target'
                  values={{
                    tag: <br />,
                  }}
                />
              </div>
            </div>
            <div className='flex h-full w-2/6 flex-col justify-between px-3'>
              {lightning.received ? (
                <img
                  className='mx-auto'
                  src='/static/i/svg/ms-lightning.svg?v=2'
                  alt='ms-lightning'
                />
              ) : (
                <MsLightningGreySvg classNameProps='mx-auto text-grey dark:text-grey-dark-default opacity-50' />
              )}
              <div className='flex justify-center font-bold'>
                {gameMode ? (
                  // eslint-disable-next-line
                  <>
                    {lightning.earned ? (
                      <DoneSvg
                        width={30}
                        height={30}
                        classNameProps='inline-block'
                        colorFill={lightningIconActive}
                      />
                    ) : (
                      <>
                        {lightning.received ? (
                          <CoinSvg classNameProps='mr-1 w-[30px]' />
                        ) : (
                          <CoinGreySvg classNameProps='mr-1 w-[30px]' />
                        )}
                        <span
                          className={classNames(
                            "text-base",
                            lightningTextColor,
                          )}
                        >
                          +{lightning.coin}
                        </span>
                      </>
                    )}
                  </>
                ) : null}
              </div>
              <div
                className={classNames(
                  "text-small-xs md:text-small",
                  lightningTextColor,
                )}
              >
                <FormattedMessage
                  id='exercise-results-lightning'
                  values={{
                    tag: <br />,
                    speed: speed2CurrentUnits(
                      Math.round(lightning.speed),
                      speedRatio,
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className='mb-6 flex justify-center'>
          <Button
            onClick={onClickPrevExercises}
            className={classNames("btn-secondary mr-4", {
              "outline outline-2 outline-offset-2 outline-state-focus-primary":
                btnPrevActive,
            })}
            ref={btnPrevRef}
          >
            <FormattedMessage id='try-again' />
          </Button>
          <Button
            className={classNames("btn-primary", {
              "outline outline-2 outline-offset-2 outline-state-focus-primary":
                btnNextActive,
            })}
            onClick={onClickNextExercises}
            ref={btnNextRef}
          >
            <FormattedMessage id='next' />
          </Button>
        </div>
        <WrapperAdsBanner page='exercise-result'>
          <div style={{ minHeight: "160px" }}>
            <AdsBanner bannerPosition={BannerPosition.FINISH_BOTTOM} />
          </div>
          <AdsBanner bannerPosition={BannerPosition.FINISH_LEFT} />
          <AdsBanner bannerPosition={BannerPosition.FINISH_RIGHT} />
        </WrapperAdsBanner>
        {isRemoveAdsLinkVisible && (
          <div className='text-center'>
            <RemoveAdsLink
              id='RemoveAds_btn_tutor_finish'
              additionalClassName='btn-secondary'
              translationID='remove-ads'
            />
          </div>
        )}
      </div>
    </section>
  );
}

export default ExerciseResults;
